import { useState, useEffect, useCallback, useRef } from 'react';
import { useHandleArrowKeyPress } from 'dibs-interiors-photos/exports/useHandleArrowKeyPress';
import { NAVIGATE } from '../helpers/useHistoryReducer';
import { CHANGE_PAGE } from '../helpers/useFacetReducer';
import { INCREASE_MODAL_PAGE, DECREASE_MODAL_PAGE } from '../helpers/usePhotoModalReducer';
import { useServerVars } from '../../shared/ServerVarsContext';

import type { UseFacetReducer } from '../helpers/useFacetReducer';
import type { UsePhotoModalReducer } from '../helpers/usePhotoModalReducer';
import type { UseHistoryReducer } from '../helpers/useHistoryReducer';

type StatePayload = {
    photoUrls: string[];
    page: number;
    modalPhotoIndex: number;
    itemsOnPage: number;
    totalResults: number;
    isOpen: boolean;
    dispatchFacetFilter: UseFacetReducer['dispatchFacetFilter'];
    dispatchModalPhoto: UsePhotoModalReducer['dispatchModalPhoto'];
    dispatchHistory: UseHistoryReducer['dispatchHistory'];
};

type StateResponse = {
    currentPhotoIndex: number;
    onNext: () => void;
    onPrev: () => void;
};

export function usePhotoContainerState({
    photoUrls,
    page,
    modalPhotoIndex,
    itemsOnPage,
    totalResults,
    isOpen,
    dispatchFacetFilter,
    dispatchModalPhoto,
    dispatchHistory,
}: StatePayload): StateResponse {
    const { isDesktop } = useServerVars();

    const pageRef = useRef(page);
    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

    useEffect(() => {
        const pageRefCurrent = pageRef.current || 1;
        const pageItemsCount = (pageRefCurrent - 1) * itemsOnPage;
        setCurrentPhotoIndex(modalPhotoIndex + pageItemsCount);
    }, [modalPhotoIndex, itemsOnPage]);

    const changePhotoIndex = useCallback(
        ({ direction }: { direction: 'next' | 'prev' }) => {
            let newPhotoIndex = 0;
            let newPage = page;

            switch (true) {
                case direction === 'next':
                    newPhotoIndex = modalPhotoIndex + 1;
                    break;
                case direction === 'prev':
                    newPhotoIndex = modalPhotoIndex - 1;
                    break;
            }

            /**
             * We should not do anything further when:
             * - on first page first photo and trying to go `prev`
             * - on last page last photo and trying to go `next`
             */
            const isFirstPhoto = modalPhotoIndex === 0 && page === 1;
            const pageItemsCount = (page - 1) * itemsOnPage;
            const isLastPhoto = modalPhotoIndex + 1 + pageItemsCount === totalResults;
            if ((isFirstPhoto && direction === 'prev') || (isLastPhoto && direction === 'next')) {
                return;
            }

            if (newPhotoIndex < 0) {
                // When doing `prev` from page > 1, we should decrease page for refetch
                newPage -= 1;
            } else if (newPhotoIndex >= itemsOnPage) {
                // When doing `next` from page !== total pages, we should increase page for refetch
                newPage += 1;
            }

            if (modalPhotoIndex !== newPhotoIndex) {
                const path = photoUrls[newPhotoIndex] || '';
                dispatchHistory({ type: NAVIGATE, path });
            }

            if (page !== newPage) {
                dispatchFacetFilter({ type: CHANGE_PAGE, page: newPage });
                if (newPage > page) {
                    dispatchModalPhoto({ type: INCREASE_MODAL_PAGE });
                } else {
                    dispatchModalPhoto({ type: DECREASE_MODAL_PAGE });
                }
            }
        },
        [
            dispatchFacetFilter,
            dispatchModalPhoto,
            dispatchHistory,
            itemsOnPage,
            totalResults,
            page,
            modalPhotoIndex,
            photoUrls,
        ]
    );

    const onNext = useCallback(() => changePhotoIndex({ direction: 'next' }), [changePhotoIndex]);
    const onPrev = useCallback(() => changePhotoIndex({ direction: 'prev' }), [changePhotoIndex]);

    useHandleArrowKeyPress({
        onLeftPress: onPrev,
        onRightPress: onNext,
        shouldHandle: isOpen && isDesktop,
    });

    return {
        currentPhotoIndex,
        onNext,
        onPrev,
    };
}
